import { useEffect, useState } from 'react';
import { C } from '../lib/theme.js';

/** Keep in sync with the `themes:` list in index.css. */
export const THEMES = [
  'nord',
  'light',
  'winter',
  'corporate',
  'lofi',
  'emerald',
  'autumn',
  'business',
  'coffee',
  'sunset',
  'forest',
  'dim',
  'night',
  'dark',
  'dracula',
  'luxury',
] as const;

const KEY = 'basil.theme';
const DEFAULT = 'dim';

/** Apply the saved (or default) theme to <html>. Call once before render. */
export function applySavedTheme(): void {
  const saved = localStorage.getItem(KEY) ?? DEFAULT;
  document.documentElement.setAttribute('data-theme', saved);
}

/** Force a theme onto <html> WITHOUT touching the user's saved preference. */
export function applyTheme(theme: string): void {
  document.documentElement.setAttribute('data-theme', theme);
}

function setTheme(theme: string): void {
  localStorage.setItem(KEY, theme);
  document.documentElement.setAttribute('data-theme', theme);
}

export function ThemePicker() {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState(
    () => document.documentElement.getAttribute('data-theme') ?? DEFAULT,
  );

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  function pick(t: string) {
    setTheme(t);
    setCurrent(t);
    setOpen(false);
  }

  return (
    <div style={{ position: 'relative' }}>
      <div
        onClick={() => setOpen((v) => !v)}
        title="Theme"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '7px 11px',
          border: `1px solid ${C.line}`,
          borderRadius: 8,
          color: C.secondary,
          font: `500 11.5px 'IBM Plex Mono',monospace`,
          cursor: 'pointer',
        }}
      >
        <span style={{ display: 'flex', gap: 3 }}>
          <Swatch v="--color-primary" />
          <Swatch v="--color-base-100" />
          <Swatch v="--color-base-content" />
        </span>
        <span style={{ textTransform: 'uppercase', letterSpacing: '.04em' }}>{current}</span>
        <span style={{ color: C.muted, fontSize: 11 }}>⌄</span>
      </div>
      {open && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 40 }} onClick={() => setOpen(false)} />
          <div
            style={{
              position: 'absolute',
              top: 'calc(100% + 6px)',
              right: 0,
              width: 210,
              maxHeight: 360,
              overflowY: 'auto',
              background: C.surface,
              border: `1px solid ${C.line}`,
              borderRadius: 10,
              padding: 5,
              zIndex: 50,
              boxShadow: '0 12px 32px rgba(0,0,0,.4)',
            }}
          >
            {THEMES.map((t) => (
              <div
                key={t}
                data-theme={t}
                onClick={() => pick(t)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '8px 10px',
                  borderRadius: 7,
                  cursor: 'pointer',
                  background: 'var(--color-base-100)',
                  color: 'var(--color-base-content)',
                  border:
                    t === current ? `1px solid var(--color-primary)` : '1px solid transparent',
                  marginBottom: 3,
                }}
              >
                <span style={{ display: 'flex', gap: 3 }}>
                  <Swatch v="--color-primary" />
                  <Swatch v="--color-secondary" />
                  <Swatch v="--color-base-content" />
                </span>
                <span style={{ fontSize: 12.5, textTransform: 'capitalize' }}>{t}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function Swatch({ v }: { v: string }) {
  return <span style={{ width: 9, height: 9, borderRadius: 2, background: `var(${v})` }} />;
}
